import { computed, isRef, type Ref } from 'vue'
import type { DisplayLayer } from '@/stores/sceneDisplayConfigs'

interface UseLayerInheritanceOptions {
  layer: DisplayLayer | Ref<DisplayLayer>
  parentLayer?: DisplayLayer | Ref<DisplayLayer | undefined>
}

/**
 * Composable to track field inheritance and provide reset functionality
 */
export function useLayerInheritance(options: UseLayerInheritanceOptions) {
  // Handle both direct values and refs
  const layer = computed(() => isRef(options.layer) ? options.layer.value : options.layer)
  const parentLayer = computed(() => 
    options.parentLayer 
      ? (isRef(options.parentLayer) ? options.parentLayer.value : options.parentLayer)
      : undefined
  )

  const isInherited = computed(() => layer.value.is_inherited === true)
  const hasOverrides = computed(() => layer.value.has_overrides === true)

  /**
   * Check if a specific field is overridden (different from parent)
   */
  const isFieldOverridden = (fieldPath: string): boolean => {
    if (!isInherited.value || !parentLayer.value) return false

    // Get nested field value
    const getFieldValue = (obj: any, path: string): any => {
      const parts = path.split('.')
      let value = obj
      for (const part of parts) {
        if (value === undefined || value === null) return undefined
        value = value[part]
      }
      return value
    }

    const currentValue = getFieldValue(layer.value, fieldPath)
    const parentValue = getFieldValue(parentLayer.value, fieldPath)

    // Deep comparison for objects
    if (typeof currentValue === 'object' && currentValue !== null && typeof parentValue === 'object' && parentValue !== null) {
      return JSON.stringify(currentValue) !== JSON.stringify(parentValue)
    }

    return currentValue !== parentValue
  }

  /**
   * Get the parent value for a field
   */
  const getParentValue = (fieldPath: string): any => {
    if (!parentLayer.value) return undefined

    const parts = fieldPath.split('.')
    let value = parentLayer.value as any
    for (const part of parts) {
      if (value === undefined || value === null) return undefined
      value = value[part]
    }
    return value
  }

  return {
    isInherited,
    hasOverrides,
    isFieldOverridden,
    getParentValue
  }
}
